/**
 * HOD Dashboard Page (Phase 2)
 * 
 * Head of Department dashboard for team oversight:
 * - CRM overview metrics
 * - Live agent status grid with OOO tracking
 * - Live report feed (WebSocket powered)
 * - Alerts and flags
 * - Task assignment to agents
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { LinkedToDisplayChip } from '../components/LinkedToSelector';
import VoiceMessageRecorder from '../components/VoiceMessageRecorder';
import {
  Users,
  TrendingUp,
  DollarSign,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MapPin,
  MessageSquare,
  FileText,
  Bell,
  RefreshCw,
  ChevronRight,
  Eye,
  Plus,
  Send,
  Home,
  Building,
  Target,
  Calendar,
  Activity,
  Zap,
  Crown,
  Reply,
  AtSign,
  LogIn,
  LogOut,
  Timer,
  X,
  XCircle,
  User,
} from 'lucide-react';

// Phase 1 CRM Enhancement Components
import AgentStatusDashboard from '../components/AgentStatusDashboard';
import ResponseTimeCard from '../components/ResponseTimeCard';
import TeamSLAMonitor from '../components/TeamSLAMonitor';

// Phase 2 CRM Enhancement Components
import LiveLocationMap from '../components/LiveLocationMap';
import TeamSiteVisitsPanel from '../components/TeamSiteVisitsPanel';
import UnifiedReportsTab from '../components/UnifiedReportsTab';
import CommentsToAgentPanel from '../components/CommentsToAgentPanel';
// Used in team scope here (the whole team's submitted visit reports);
// the agent-side My Dashboard renders the same component with scope="mine".
import SiteVisitReportsPanel from '../components/SiteVisitReportsPanel';
import AgentTalktimePanel from '../components/AgentTalktimePanel';
import HelpInfo from '../components/HelpInfo';
import DirhamSign from '../components/DirhamSign';
import AlertsFlagsPanel from '../components/AlertsFlagsPanel';
import MarketingDashboardTab from '../components/MarketingDashboardTab';
import MarketingDashboardV2 from './MarketingDashboardV2';
import {
  SalesSnapshotPanel as SharedSalesSnapshotPanel,
  MarketingSnapshotPanel as SharedMarketingSnapshotPanel,
} from '../components/SnapshotPanels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Read-only view of a submitted agent visit report. Surfaces Outcome,
// Notes, Next steps and the AI narrative the agent (or AI) wrote up after
// the visit.
const AgentMovementOpenReportButton = ({ movement, darkMode }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setOpen(true)}>
        <Eye size={11} className="mr-1" /> Open Report
      </Button>
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            backgroundColor: 'rgba(15,23,42,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border p-6 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold">Site Visit Report</h2>
                <p className="text-xs text-slate-500">
                  {movement.agent_name} · {movement.location}
                  {movement.duration_seconds != null && <> · {Math.floor(movement.duration_seconds / 60)}m on site</>}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {movement.report_outcome && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Outcome</p>
                  <p>{movement.report_outcome}</p>
                </div>
              )}
              {movement.report_notes && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Agent notes</p>
                  <p className="whitespace-pre-wrap">{movement.report_notes}</p>
                </div>
              )}
              {movement.report_next_steps && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Next steps</p>
                  <p className="whitespace-pre-wrap">{movement.report_next_steps}</p>
                </div>
              )}
              {movement.ai_narrative && (
                <div>
                  <p className="text-xs font-semibold uppercase text-emerald-700">AI narrative</p>
                  <div className="whitespace-pre-wrap rounded-md bg-emerald-50 border border-emerald-200 p-3 text-slate-800">
                    {movement.ai_narrative}
                  </div>
                </div>
              )}
              {!movement.report_outcome && !movement.report_notes && !movement.ai_narrative && (
                <p className="text-sm text-slate-500">No report content available.</p>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Feedback composer for an agent who skipped their post-visit report.
// Sends directly to that agent — the HOD is already the sender, so no
// extra fan-out is needed.
const AgentMovementFeedbackButton = ({ movement, token, darkMode }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const start = () => {
    setOpen(true);
    setText(
      `Heads up — you skipped the post-visit report for ${movement.location || 'today\'s site'}.`
      + `\n\nPlease open the visit on your dashboard and submit the report (Outcome / Notes / Next steps) now. Reply here once it's filed.`
    );
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Add a message before sending'); return; }
    if (!movement.agent_id) { toast.error('No agent id on this row'); return; }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/hod/agents/${movement.agent_id}/direct-message`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_text: text, agent_id: movement.agent_id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Feedback sent to ${movement.agent_name || 'agent'}`);
        setOpen(false);
      } else {
        toast.error(data.detail || 'Failed to send');
      }
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button size="sm" className="h-6 text-xs px-2 bg-amber-600 hover:bg-amber-500 text-white" onClick={start}>
        <Send size={11} className="mr-1" /> Send Feedback
      </Button>
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            backgroundColor: 'rgba(15,23,42,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !sending) setOpen(false); }}
        >
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border p-6 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-lg font-semibold">Send feedback for skipped report</h2>
                <p className="text-xs text-slate-500">
                  {movement.agent_name} · {movement.location}
                </p>
              </div>
              <button onClick={() => !sending && setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Write a short message…"
              className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
              disabled={sending}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {sending ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                Send Feedback
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============ CRM Overview Panel ============
const CRMOverviewPanel = ({ data, loading, selectedView = 'all' }) => {
  const { isDarkMode } = useTheme();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className={isDarkMode ? 'bg-card border-border animate-pulse' : 'animate-pulse'}>
            <CardContent className="p-4 h-24" />
          </Card>
        ))}
      </div>
    );
  }

  // Sales-specific metrics
  const salesMetrics = [
    {
      label: 'Monthly Revenue',
      value: <><DirhamSign /> {(data?.revenue?.this_month || 0).toLocaleString()}</>,
      subtext: `${data?.revenue?.deals_closed || 0} deals closed`,
      // Dirham mark replaces the lucide DollarSign in the green pill so
      // the icon matches the currency the value is denominated in.
      icon: DirhamSign,
      color: 'text-emerald-500',
      bgColor: isDarkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'
    },
    {
      label: 'Active Deals',
      value: Object.values(data?.deals_by_stage || {}).reduce((acc, s) => acc + (s.count || 0), 0),
      subtext: <><DirhamSign /> {Object.values(data?.deals_by_stage || {}).reduce((acc, s) => acc + (s.value || 0), 0).toLocaleString()} in pipeline</>,
      icon: Target,
      color: 'text-blue-500',
      bgColor: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'
    },
    {
      label: 'New Leads',
      value: Object.values(data?.leads_by_source || {}).reduce((acc, c) => acc + c, 0),
      subtext: `${Object.keys(data?.leads_by_source || {}).length} sources`,
      icon: Users,
      color: 'text-purple-500',
      bgColor: isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'
    },
    {
      label: 'Units Sold',
      value: data?.units?.sold || 0,
      subtext: `${data?.units?.available || 0} available`,
      icon: Building,
      color: 'text-amber-500',
      bgColor: isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50'
    }
  ];

  // Marketing-specific metrics
  const marketingMetrics = [
    {
      label: 'Campaign Reach',
      value: '12.5K',
      subtext: '+18% from last month',
      icon: TrendingUp,
      color: 'text-purple-500',
      bgColor: isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'
    },
    {
      label: 'Lead Conversions',
      value: '342',
      subtext: '4.2% conversion rate',
      icon: Target,
      color: 'text-emerald-500',
      bgColor: isDarkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'
    },
    {
      label: 'Ad Spend',
      value: <><DirhamSign /> 8,450</>,
      subtext: <>Cost per lead: <DirhamSign /> 24.70</>,
      icon: DirhamSign,
      color: 'text-blue-500',
      bgColor: isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'
    },
    {
      label: 'Email Open Rate',
      value: '28.5%',
      subtext: '15,200 emails sent',
      icon: MessageSquare,
      color: 'text-amber-500',
      bgColor: isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50'
    }
  ];

  // Select metrics based on view
  const metrics = selectedView === 'marketing' ? marketingMetrics : salesMetrics;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {metrics.map((metric, idx) => (
        <Card key={idx} className={`${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {metric.label}
                </p>
                <p className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {metric.value}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {metric.subtext}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                <metric.icon className={metric.color} size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============ Agent Status Grid ============
const AgentStatusGrid = ({ agents, loading, onViewAgent }) => {
  const { isDarkMode } = useTheme();

  const getStatusBadge = (status) => {
    const statusConfig = {
      in_office: { label: 'In Office', variant: 'success', color: 'bg-emerald-500' },
      ooo: { label: 'Out of Office', variant: 'warning', color: 'bg-amber-500' },
      idle: { label: 'Idle', variant: 'secondary', color: 'bg-slate-400' },
      no_activity: { label: 'No Activity', variant: 'outline', color: 'bg-slate-300' }
    };
    const config = statusConfig[status] || statusConfig.no_activity;
    return (
      <Badge variant={config.variant} className="text-xs">
        <span className={`w-2 h-2 rounded-full ${config.color} mr-1.5`} />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className={isDarkMode ? 'bg-card border-border animate-pulse' : 'animate-pulse'}>
            <CardContent className="p-4 h-32" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {agents?.map((agent) => (
        <Card 
          key={agent.id} 
          className={`cursor-pointer transition-all hover:shadow-md ${
            agent.current_status === 'ooo' 
              ? isDarkMode ? 'bg-amber-900/10 border-amber-700/50' : 'bg-amber-50 border-amber-200'
              : isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''
          }`}
          onClick={() => onViewAgent(agent)}
          data-testid={`agent-card-${agent.id}`}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${
                  agent.current_status === 'ooo' ? 'bg-amber-500' : 'bg-violet-500'
                }`}>
                  {agent.full_name?.charAt(0) || agent.email?.charAt(0) || '?'}
                </div>
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.full_name || agent.email}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {agent.email}
                  </p>
                </div>
              </div>
              {getStatusBadge(agent.current_status)}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {agent.leads_assigned || 0}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Leads</p>
              </div>
              <div>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {agent.deals_closed || 0}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Closed</p>
              </div>
              <div>
                <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {agent.conversion_rate || 0}%
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Conv.</p>
              </div>
            </div>

            {agent.current_status === 'ooo' && agent.ooo_info && (
              <div className={`mt-3 p-2 rounded text-xs ${isDarkMode ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
                <div className="flex items-center gap-1">
                  <MapPin size={12} className="text-amber-500" />
                  <span>{agent.ooo_info.destination}</span>
                </div>
                {agent.ooo_info.expected_return_at && (
                  <p className={isDarkMode ? 'text-amber-400' : 'text-amber-600'}>
                    Expected back: {agent.ooo_info.expected_return_at}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============ Live Report Feed ============
const LiveReportFeed = ({ reports, loading, onViewReport }) => {
  const { isDarkMode } = useTheme();

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} className={isDarkMode ? 'bg-card border-border animate-pulse' : 'animate-pulse'}>
            <CardContent className="p-4 h-24" />
          </Card>
        ))}
      </div>
    );
  }

  if (!reports?.length) {
    return (
      <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        <FileText size={48} className="mx-auto mb-4 opacity-50" />
        <p>No reports generated today yet</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-3 pr-4">
        {reports.map((report) => (
          <Card 
            key={report.id}
            className={`cursor-pointer transition-all hover:shadow-md ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}
            onClick={() => onViewReport(report)}
            data-testid={`report-card-${report.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center text-white font-medium">
                    {report.agent?.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {report.agent?.full_name || 'Unknown Agent'}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {new Date(report.generated_at).toLocaleTimeString()} • v{report.version}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {report.hod_viewed ? (
                    <Badge variant="outline" className="text-xs text-emerald-600">
                      <Eye size={12} className="mr-1" /> Viewed
                    </Badge>
                  ) : (
                    <Badge variant="default" className="text-xs">New</Badge>
                  )}
                </div>
              </div>

              <div className={`mt-3 text-sm line-clamp-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {report.report_text?.substring(0, 150)}...
              </div>

              <div className="mt-2 flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {report.trigger_type?.replace(/_/g, ' ')}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

// Tiny Markdown-aware renderer for CEO comment bodies. Handles the subset
// produced by the Executive Brief share — headings (`#`/`##`/`###`), bullet
// lines (`- `), inline bold (`**foo**`), bold key/value (`- **key**: val`)
// and preserves explicit line breaks. Anything else falls through as text.
const RichComment = ({ text, isDarkMode }) => {
  if (!text) return null;
  const baseClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const mutedClass = isDarkMode ? 'text-slate-300' : 'text-slate-700';

  // Render inline **bold** segments inside a single line.
  const renderInline = (line) => {
    const parts = [];
    const re = /\*\*(.+?)\*\*/g;
    let lastIdx = 0;
    let m;
    let key = 0;
    while ((m = re.exec(line)) !== null) {
      if (m.index > lastIdx) parts.push(<span key={`t-${key++}`}>{line.slice(lastIdx, m.index)}</span>);
      parts.push(<strong key={`b-${key++}`}>{m[1]}</strong>);
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < line.length) parts.push(<span key={`t-${key++}`}>{line.slice(lastIdx)}</span>);
    return parts.length ? parts : line;
  };

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className={`list-disc pl-5 space-y-1 my-2 text-sm ${mutedClass}`}>
          {bullets.map((b, i) => <li key={i}>{renderInline(b)}</li>)}
        </ul>
      );
      bullets = [];
    }
  };

  lines.forEach((rawLine, idx) => {
    const line = rawLine.replace(/\s+$/, '');
    if (!line.trim()) {
      flushBullets();
      return;
    }
    if (line.startsWith('### ')) {
      flushBullets();
      blocks.push(
        <h4 key={`h-${idx}`} className={`text-sm font-semibold mt-3 mb-1 ${baseClass}`}>{renderInline(line.slice(4))}</h4>
      );
    } else if (line.startsWith('## ')) {
      flushBullets();
      blocks.push(
        <h3 key={`h-${idx}`} className={`text-base font-semibold mt-3 mb-1 ${baseClass}`}>{renderInline(line.slice(3))}</h3>
      );
    } else if (line.startsWith('# ')) {
      flushBullets();
      blocks.push(
        <h2 key={`h-${idx}`} className={`text-lg font-bold mt-3 mb-1 ${baseClass}`}>{renderInline(line.slice(2))}</h2>
      );
    } else if (/^\s*[-*]\s+/.test(line)) {
      bullets.push(line.replace(/^\s*[-*]\s+/, ''));
    } else {
      flushBullets();
      blocks.push(
        <p key={`p-${idx}`} className={`text-sm my-1 whitespace-pre-wrap ${baseClass}`}>{renderInline(line)}</p>
      );
    }
  });
  flushBullets();
  return <div className="space-y-1">{blocks}</div>;
};

// ============ CEO Comments Panel ============
const CEOCommentsPanel = ({ comments, loading, onReply, onMarkRead, onMarkAllRead, userRole, onConversationReply }) => {
  const { isDarkMode } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replying, setReplying] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  
  // Check if current user is CEO or Admin or HOD - ALL can reply
  const isCEO = userRole === 'ceo';
  const isAdmin = userRole === 'super_admin';
  const isHOD = userRole === 'hod_sales' || userRole === 'hod_marketing';
  const canReply = isCEO || isAdmin || isHOD; // Everyone can reply
  
  // Handle clicking on linked record chips - navigates to the relevant CRM record
  const handleLinkedRecordClick = (linkedTo) => {
    if (!linkedTo || linkedTo.isDeleted) return;
    
    const { recordType, recordId } = linkedTo;
    
    switch (recordType) {
      case 'agent':
        // Navigate to team/users with the agent highlighted
        navigate(`/users?highlight=${recordId}`);
        break;
      case 'deal':
        // Navigate to analytics with deal filter
        navigate(`/analytics?deal=${recordId}`);
        break;
      case 'lead':
        // Navigate to contact detail page
        navigate(`/contacts/${recordId}`);
        break;
      case 'report':
        // Navigate to agent daily report or dashboard
        navigate(`/hod-dashboard?report=${recordId}`);
        break;
      case 'campaign':
        // Navigate to marketing campaign
        navigate(`/marketing/campaigns/${recordId}`);
        break;
      default:
        // Fallback - show toast with record info
        toast.info(`Record: ${linkedTo.recordLabel || 'Unknown'}`);
    }
  };

  // Fetch users for @mentions (CEO + HODs)
  useEffect(() => {
    const fetchMentionableUsers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/hods`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          const users = data.data?.hods || [];
          // Separate CEO and HODs for organized display
          const ceoUsers = users.filter(u => u.role === 'ceo' || u.role === 'super_admin');
          const hodUsers = users.filter(u => u.role === 'hod_sales' || u.role === 'hod_marketing');
          
          // Build mentionable list with special options
          const mentionList = [
            { id: 'all_hods', name: 'All HODs', role: 'group', isSpecial: true },
            ...ceoUsers.map(u => ({ ...u, roleLabel: 'CEO' })),
            ...hodUsers.map(u => ({ ...u, roleLabel: u.role === 'hod_sales' ? 'HOD Sales' : 'HOD Marketing' }))
          ];
          setMentionableUsers(mentionList);
        }
      } catch (error) {
        console.error('Failed to fetch mentionable users:', error);
      }
    };
    if (token) fetchMentionableUsers();
  }, [token]);

  // Format date as "22 Apr 2026, 02:35 PM"
  // The voice-message endpoint returns timestamps without a timezone suffix
  // (Mongo stores BSON dates as UTC; FastAPI's default JSONEncoder emits a
  // naive ISO string). Browsers vary on whether they parse a tz-less string
  // as local or UTC. Normalising to UTC here keeps every screen identical.
  const parseAsUTC = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date) return raw;
    const s = String(raw);
    const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
    return new Date(hasTimezone ? s : `${s}Z`);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = parseAsUTC(dateStr);
    if (!date || Number.isNaN(date.getTime())) return '';
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day} ${month} ${year}, ${time}`;
  };

  // Get role label for display
  const getRoleLabel = (senderRole) => {
    if (senderRole === 'ceo' || senderRole === 'super_admin') {
      return 'CEO Reply';
    } else if (senderRole === 'hod_sales') {
      return 'HOD Sales Reply';
    } else if (senderRole === 'hod_marketing') {
      return 'HOD Marketing Reply';
    } else {
      return 'HOD Reply';
    }
  };

  // Handle @mention input
  const handleReplyTextChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart || value.length;
    setReplyText(value);
    setCursorPosition(position);

    // Check if user is typing @mention - improved detection
    const textBeforeCursor = value.substring(0, position);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    // Show dropdown if @ is found and it's at start or after a space
    if (lastAtIndex !== -1) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);
        // Only show if search text doesn't contain space (user is still typing the mention)
        if (!searchText.includes(' ')) {
          setMentionFilter(searchText.toLowerCase());
          setShowMentions(true);
          return;
        }
      }
    }
    setShowMentions(false);
  };

  // Insert @mention
  const insertMention = (user) => {
    const textBeforeCursor = replyText.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterMention = replyText.substring(cursorPosition);
    
    const userTag = user.isSpecial 
      ? `@${user.name.replace(/\s+/g, '_')} `
      : `@${user.name.replace(/\s+/g, '_')} `;
    const newText = textBeforeCursor.substring(0, atIndex) + userTag + textAfterMention;
    
    setReplyText(newText);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  // Extract tagged users from comment text with role identification
  const extractTaggedUsers = (text, targetHod) => {
    const tagged = [];
    
    // Check for target_hod field
    if (targetHod === 'all') {
      tagged.push({ name: 'All HODs', role: 'group' });
    } else if (targetHod === 'hod_sales') {
      tagged.push({ name: 'HOD Sales', role: 'hod_sales' });
    } else if (targetHod === 'hod_marketing') {
      tagged.push({ name: 'HOD Marketing', role: 'hod_marketing' });
    }
    
    // Extract @mentions from text
    if (text) {
      const mentions = text.match(/@[\w_]+/g) || [];
      mentions.forEach(m => {
        const name = m.substring(1).replace(/_/g, ' ');
        // Find user in mentionableUsers to get role
        const user = mentionableUsers.find(u => 
          u.name?.toLowerCase().replace(/\s+/g, ' ') === name.toLowerCase()
        );
        if (name === 'All HODs') {
          tagged.push({ name: 'All HODs', role: 'group' });
        } else if (user) {
          tagged.push({ name: user.name, role: user.role, roleLabel: user.roleLabel });
        } else {
          tagged.push({ name, role: 'unknown' });
        }
      });
    }
    
    return tagged;
  };

  // Get role display label
  const getTagRoleLabel = (user) => {
    if (user.role === 'group') return '';
    if (user.role === 'ceo' || user.role === 'super_admin') return 'CEO';
    if (user.role === 'hod_sales') return 'HOD Sales';
    if (user.role === 'hod_marketing') return 'HOD Marketing';
    return user.roleLabel || user.role?.replace(/_/g, ' ') || '';
  };

  // Filter users for autocomplete
  const filteredUsers = mentionableUsers.filter(user => 
    user.name?.toLowerCase().includes(mentionFilter) ||
    user.role?.toLowerCase().includes(mentionFilter) ||
    user.roleLabel?.toLowerCase().includes(mentionFilter)
  );

  const handleSendMessage = async (commentId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      if (onConversationReply) {
        await onConversationReply(commentId, replyText);
      } else {
        await onReply(commentId, replyText);
      }
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
    setReplying(false);
  };

  const unreadCount = comments?.filter(c => !c.is_read_by_hod).length || 0;
  const urgentUnreadCount = comments?.filter(c => !c.is_read_by_hod && c.priority === 'urgent').length || 0;

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className={`p-3 rounded-lg ${isDarkMode ? 'bg-muted' : 'bg-slate-100'} animate-pulse h-20`} />
        ))}
      </div>
    );
  }

  if (!comments?.length) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        <Crown size={40} className="mx-auto mb-2 text-violet-500 opacity-50" />
        <p>No comments from CEO yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Mark All Read */}
      {unreadCount > 0 && (
        <div className="flex items-center justify-between">
          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {unreadCount} unread {urgentUnreadCount > 0 && `(${urgentUnreadCount} urgent)`}
          </span>
          {isHOD && (
            <Button variant="ghost" size="sm" onClick={onMarkAllRead}>
              <CheckCircle2 size={14} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>
      )}
      
      <ScrollArea className="h-[400px]">
        <div className="space-y-3 pr-4">
          {comments.map((comment) => comment._kind === 'voice' ? (
            <div
              key={`voice-${comment.id}`}
              className={`p-4 rounded-lg border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
              data-testid={`ceo-voice-note-${comment.id}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Crown size={16} className="text-violet-500" />
                <span className={`text-xs font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                  Voice note from CEO
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  · {Math.round(comment.duration_seconds || 0)}s
                </span>
                {comment.target_hod && (
                  <Badge
                    variant="outline"
                    className={`text-xs ${
                      comment.target_hod === 'hod_sales' ? 'border-blue-500 text-blue-600' :
                      comment.target_hod === 'hod_marketing' ? 'border-purple-500 text-purple-600' :
                      'border-amber-500 text-amber-600 bg-amber-50'
                    }`}
                  >
                    → {comment.target_hod === 'hod_sales' ? 'HOD Sales' :
                       comment.target_hod === 'hod_marketing' ? 'HOD Marketing' : 'All HODs'}
                  </Badge>
                )}
              </div>
              <audio
                controls
                preload="none"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/voice-messages/${comment.id}/audio`}
                className="w-full"
              />
              <div className={`flex items-center gap-2 mt-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Clock size={12} />
                <span>{formatDateTime(comment.created_at)}</span>
              </div>
            </div>
          ) : (
            <div
              key={comment.id}
              className={`p-4 rounded-lg border ${
                comment.priority === 'urgent'
                  ? !comment.is_read_by_hod
                    ? isDarkMode ? 'bg-red-900/30 border-red-700 animate-pulse' : 'bg-red-100 border-red-300 animate-pulse'
                    : isDarkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                  : !comment.is_read_by_hod
                    ? isDarkMode ? 'bg-violet-900/20 border-violet-700' : 'bg-violet-50 border-violet-200'
                    : isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
              }`}
              data-testid={`ceo-comment-${comment.id}`}
            >
              {/* Comment Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Crown size={16} className="text-violet-500" />
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                    From CEO
                  </span>
                  {/* Linked Record Display */}
                  {comment.linked_to && typeof comment.linked_to === 'object' && comment.linked_to.recordLabel ? (
                    <LinkedToDisplayChip 
                      linkedTo={comment.linked_to}
                      onClick={(linked) => handleLinkedRecordClick(linked)}
                    />
                  ) : comment.linked_to && typeof comment.linked_to === 'string' ? (
                    // Legacy: plain string linked_to
                    <Badge variant="outline" className="text-xs">
                      {comment.linked_to}
                    </Badge>
                  ) : null}
                  {/* Tagged Users Display - Clear role identification */}
                  {(() => {
                    const taggedUsers = extractTaggedUsers(comment.comment_text, comment.target_hod);
                    if (taggedUsers.length === 0) return null;
                    return (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tagged:</span>
                        {taggedUsers.map((user, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className={`text-xs ${
                              user.role === 'group' 
                                ? isDarkMode ? 'border-amber-500 text-amber-400 bg-amber-900/20' : 'border-amber-500 text-amber-600 bg-amber-50'
                                : user.role === 'ceo' || user.role === 'super_admin'
                                  ? isDarkMode ? 'border-violet-500 text-violet-400 bg-violet-900/20' : 'border-violet-500 text-violet-600 bg-violet-50'
                                  : isDarkMode ? 'border-cyan-500 text-cyan-400 bg-cyan-900/20' : 'border-cyan-500 text-cyan-600 bg-cyan-50'
                            }`}
                          >
                            <AtSign size={10} className="mr-1" />
                            {user.role === 'group' ? (
                              <span className="font-semibold">All HODs</span>
                            ) : (
                              <>
                                <span className="font-medium">{getTagRoleLabel(user)}</span>
                                {user.name && <span className="ml-1">– {user.name}</span>}
                              </>
                            )}
                          </Badge>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={comment.priority === 'urgent' ? 'destructive' : 'secondary'} className="text-xs">
                    {comment.priority}
                  </Badge>
                  {!comment.is_read_by_hod && (
                    <Badge variant="default" className="text-xs bg-violet-500">New</Badge>
                  )}
                </div>
              </div>

              {/* Comment Text — render lightweight Markdown so shared briefs
                  show up as structured headings and bullet points instead
                  of one big blob. */}
              <RichComment text={comment.comment_text} isDarkMode={isDarkMode} />


              {/* Comment Date & Time */}
              <div className={`flex items-center gap-2 mt-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <Clock size={12} />
                <span>{formatDateTime(comment.created_at)}</span>
                {/* Seen status for HOD viewing */}
                {isHOD && comment.is_read_by_hod && (
                  <span className="ml-2 text-emerald-500">
                    <Eye size={12} className="inline mr-1" />
                    Seen
                  </span>
                )}
              </div>

              {/* Conversation Thread */}
              {comment.conversation && comment.conversation.length > 0 && (
                <div className={`mt-4 space-y-3 ${isDarkMode ? 'border-l-2 border-slate-600' : 'border-l-2 border-slate-300'} pl-3`}>
                  {comment.conversation.map((msg, idx) => {
                    const isCEOReply = msg.sender_role === 'ceo' || msg.sender_role === 'super_admin';
                    return (
                      <div key={msg.id || idx} className={`p-3 rounded-lg ${
                        isCEOReply
                          ? isDarkMode ? 'bg-violet-900/30 border border-violet-700/50' : 'bg-violet-50 border border-violet-200'
                          : isDarkMode ? 'bg-emerald-900/30 border border-emerald-700/50' : 'bg-emerald-50 border border-emerald-200'
                      }`}>
                        {/* Reply Role Label */}
                        <p className={`text-xs font-semibold mb-1 ${
                          isCEOReply
                            ? isDarkMode ? 'text-violet-400' : 'text-violet-600'
                            : isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                        }`}>
                          {getRoleLabel(msg.sender_role)}
                        </p>
                        
                        {/* Reply Message */}
                        <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                          {msg.message}
                        </p>
                        
                        {/* Reply Date/Time & Seen Status */}
                        <div className={`flex items-center gap-2 mt-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <Clock size={11} />
                          <span>{formatDateTime(msg.timestamp)}</span>
                          
                          {/* Seen Status */}
                          {msg.seen_at ? (
                            <span className="ml-2 text-emerald-500">
                              <Eye size={11} className="inline mr-1" />
                              Seen at {formatDateTime(msg.seen_at)}
                            </span>
                          ) : (
                            /* Show "Seen by CEO" for HOD replies when CEO has seen them */
                            !isCEOReply && comment.is_ceo_read_reply && (
                              <span className="ml-2 text-emerald-500">
                                <Eye size={11} className="inline mr-1" />
                                Seen by CEO
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Legacy HOD Reply (for old comments without conversation array) */}
              {!comment.conversation?.length && comment.hod_reply && (
                <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-emerald-900/30 border border-emerald-700/50' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <p className={`text-xs font-semibold mb-1 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    HOD Reply
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    {comment.hod_reply}
                  </p>
                  <div className={`flex items-center gap-2 mt-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Clock size={11} />
                    <span>{formatDateTime(comment.hod_replied_at)}</span>
                    {comment.is_ceo_read_reply && (
                      <span className="ml-2 text-emerald-500">
                        <Eye size={11} className="inline mr-1" />
                        Seen by CEO
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Reply Button - ALWAYS Visible for CEO and HODs */}
              <div className="mt-4">
                {replyingTo === comment.id ? (
                  <div className="space-y-2 relative">
                    <div className="relative">
                      <Textarea
                        ref={textareaRef}
                        value={replyText}
                        onChange={handleReplyTextChange}
                        placeholder="Type @ to tag CEO or HODs... Write your reply"
                        rows={2}
                        className="text-sm"
                        data-testid="comment-reply-textarea"
                      />
                      
                      {/* @Mention Dropdown - Shows CEO, All HODs, and individual HODs */}
                      {showMentions && (filteredUsers.length > 0 || mentionableUsers.length > 0) && (
                        <div className={`absolute z-50 w-full mt-1 rounded-md border shadow-lg max-h-48 overflow-y-auto ${
                          isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
                        }`} data-testid="mention-dropdown">
                          {(filteredUsers.length > 0 ? filteredUsers : mentionableUsers).map((user, idx) => (
                            <button
                              key={user.id || idx}
                              type="button"
                              onClick={() => insertMention(user)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
                                user.isSpecial 
                                  ? isDarkMode ? 'bg-amber-900/30 hover:bg-amber-900/50 text-amber-300 border-b border-slate-600' : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-b border-slate-200'
                                  : isDarkMode ? 'hover:bg-slate-700 text-white' : 'hover:bg-slate-100 text-slate-900'
                              }`}
                              data-testid={`mention-option-${user.id || user.name.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              <AtSign size={14} className={user.isSpecial ? 'text-amber-500' : 'text-violet-500'} />
                              <span className="font-medium">{user.name}</span>
                              {!user.isSpecial && (
                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  ({user.roleLabel || user.role?.replace(/_/g, ' ')})
                                </span>
                              )}
                              {user.isSpecial && (
                                <Badge variant="outline" className="ml-auto text-xs border-amber-500 text-amber-500">
                                  Group
                                </Badge>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleSendMessage(comment.id)}
                        disabled={replying || !replyText.trim()}
                        className={isCEO || isAdmin ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                        data-testid="send-reply-btn"
                      >
                        {replying ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                        Send Reply
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                          setShowMentions(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {/* Reply Button - ALWAYS visible for CEO and HODs */}
                    {canReply && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setReplyingTo(comment.id)}
                        className={isCEO || isAdmin 
                          ? isDarkMode ? 'border-violet-500 text-violet-400 hover:bg-violet-900/30' : 'border-violet-500 text-violet-600 hover:bg-violet-50'
                          : isDarkMode ? 'border-emerald-500 text-emerald-400 hover:bg-emerald-900/30' : 'border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                        }
                        data-testid="reply-btn"
                      >
                        <Reply size={14} className="mr-1" />
                        Reply
                      </Button>
                    )}
                    {/* Mark Read button for HODs only */}
                    {isHOD && !comment.is_read_by_hod && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onMarkRead(comment.id)}
                        data-testid="mark-read-btn"
                      >
                        Mark Read
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============ Task Creation Dialog ============
const CreateTaskDialog = ({ agents, isOpen, onClose, onCreateTask, creating, noun = 'Agent' }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    agent_id: '',
    type: 'task',
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    due_time: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.agent_id || !formData.title) {
      toast.error('Please select an agent and enter a title');
      return;
    }
    onCreateTask(formData);
  };

  const canSubmit = !!formData.agent_id && !!formData.title.trim() && !creating;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-lg p-0 overflow-hidden ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
        {/* Header — icon + title + subtitle. Matches CEO Dashboard's
            Assign Task capsule so both dashboards share one look. */}
        <DialogHeader className={`px-6 pt-6 pb-4 border-b ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'bg-gradient-to-r from-violet-50 to-white'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
              <Plus size={20} />
            </div>
            <div className="flex-1">
              <DialogTitle className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                Assign Task
              </DialogTitle>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Delegate a task or reminder directly to a {noun.toLowerCase()}.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {/* Body */}
          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            {/* Agent picker */}
            <div className="space-y-1.5">
              <label className={`text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <User size={12} /> Assign to
              </label>
              <Select
                value={formData.agent_id}
                onValueChange={(v) => setFormData({ ...formData, agent_id: v })}
              >
                <SelectTrigger className="h-10" data-testid="task-agent-select">
                  <SelectValue placeholder={`Pick a ${noun.toLowerCase()}…`} />
                </SelectTrigger>
                <SelectContent>
                  {(!agents || agents.length === 0) ? (
                    <SelectItem value="__none__" disabled>No {noun.toLowerCase()}s available</SelectItem>
                  ) : agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.full_name || agent.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type + Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Type
                </label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Priority
                </label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Title
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Follow up with Bayz 102 buyers"
                required
                className="h-10"
                data-testid="task-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className={`text-[11px] uppercase tracking-wider font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={`Optional notes for the ${noun.toLowerCase()}`}
                rows={3}
                className="resize-none"
              />
            </div>

            {/* When — date + time grouped in a single soft card */}
            <div className={`rounded-lg border p-4 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/60'}`}>
              <div className={`text-[11px] uppercase tracking-wider font-semibold mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                When
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Due date</label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className={`h-10 ${isDarkMode ? '' : 'bg-white'}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={`text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Due time</label>
                  <Input
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                    className={`h-10 ${isDarkMode ? '' : 'bg-white'}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className={`px-6 py-4 border-t gap-2 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'bg-slate-50/70'}`}>
            <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              data-testid="task-submit-btn"
            >
              {creating ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Assigning…</>
              ) : (
                <><Plus size={14} className="mr-2" /> Assign Task</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ Report Detail Dialog ============
const ReportDetailDialog = ({ report, isOpen, onClose, onAddNote, onSendFeedback, onMarkViewed }) => {
  const { isDarkMode } = useTheme();
  const [noteText, setNoteText] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [markingViewed, setMarkingViewed] = useState(false);
  const { token } = useAuth();

  // Fetch feedback when dialog opens
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!isOpen || !report?.id || !token) return;
      setLoadingFeedback(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hod/reports/${report.id}/feedback`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setFeedback(data.data.feedback || []);
        }
      } catch (error) {
        console.error('Failed to fetch feedback:', error);
      }
      setLoadingFeedback(false);
    };
    fetchFeedback();
  }, [isOpen, report?.id, token]);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    await onAddNote(report.id, noteText);
    setNoteText('');
    setAddingNote(false);
  };

  // Handle mark as viewed
  const handleMarkViewed = async () => {
    if (!report?.id || report.hod_viewed) return;
    setMarkingViewed(true);
    try {
      await onMarkViewed(report.id);
    } catch (error) {
      console.error('Failed to mark as viewed:', error);
    }
    setMarkingViewed(false);
  };

  const handleSendFeedback = async () => {
    if (!feedbackText.trim() || !report?.agent_id) return;
    setSendingFeedback(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hod/reports/${report.id}/feedback`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feedback_text: feedbackText,
          agent_id: report.agent_id
        })
      });
      const data = await res.json();
      if (data.success) {
        setFeedback(prev => [data.data.feedback, ...prev]);
        setFeedbackText('');
      }
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
    setSendingFeedback(false);
  };

  if (!report) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={20} />
            Daily Report - {report.agent?.full_name || 'Agent'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
              Generated: {new Date(report.generated_at).toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              {report.hod_viewed ? (
                <Badge variant="outline" className="text-xs text-emerald-600">
                  <Eye size={12} className="mr-1" /> Marked as Read
                </Badge>
              ) : (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleMarkViewed}
                  disabled={markingViewed}
                  className="text-xs"
                  data-testid="mark-viewed-btn"
                >
                  {markingViewed ? (
                    <RefreshCw size={12} className="mr-1 animate-spin" />
                  ) : (
                    <Eye size={12} className="mr-1" />
                  )}
                  Mark as Read
                </Button>
              )}
              <Badge variant="secondary">v{report.version}</Badge>
            </div>
          </div>

          <ScrollArea className="h-[400px] rounded-lg border p-4">
            <div className={`prose prose-sm max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: report.report_text
                    ?.replace(/\n/g, '<br/>')
                    .replace(/## /g, '<h3>')
                    .replace(/- /g, '&bull; ')
                }}
              />
            </div>
          </ScrollArea>

          <div className={`border-t pt-4 ${isDarkMode ? 'border-border' : ''}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Add Private Note (not visible to agent)
            </h4>
            <div className="flex gap-2">
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a note about this report..."
                rows={2}
                className="flex-1"
              />
              <Button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}>
                {addingNote ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
              </Button>
            </div>
          </div>

          {report.hod_notes?.length > 0 && (
            <div>
              <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Your Notes
              </h4>
              <div className="space-y-2">
                {report.hod_notes.map((note, idx) => (
                  <div 
                    key={idx}
                    className={`p-2 rounded text-sm ${isDarkMode ? 'bg-muted' : 'bg-slate-100'}`}
                  >
                    <p>{note.note_text}</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Feedback to Agent Section */}
          <div className={`border-t pt-4 ${isDarkMode ? 'border-border' : ''}`}>
            <h4 className={`text-sm font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              <MessageSquare size={16} />
              Send Feedback to Agent (visible to agent)
            </h4>
            <div className="flex gap-2">
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Write feedback for the agent..."
                rows={2}
                className="flex-1"
              />
              <Button
                onClick={handleSendFeedback}
                disabled={sendingFeedback || !feedbackText.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {sendingFeedback ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
              <VoiceMessageRecorder
                chatType="agent_feedback"
                contactId={report?.agent_id || null}
                compact
              />
            </div>
          </div>

          {/* Feedback History */}
          {feedback.length > 0 && (
            <div>
              <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Feedback Sent
              </h4>
              <div className="space-y-3 max-h-[200px] overflow-y-auto">
                {feedback.map((fb) => (
                  <div 
                    key={fb.id}
                    className={`p-3 rounded-lg border ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-emerald-50 border-emerald-200'}`}
                  >
                    <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{fb.feedback_text}</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Sent: {new Date(fb.created_at).toLocaleString()}
                    </p>
                    {fb.agent_reply && (
                      <div className={`mt-2 pl-3 border-l-2 ${isDarkMode ? 'border-violet-500' : 'border-violet-400'}`}>
                        <p className={`text-sm ${isDarkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                          <span className="font-medium">Agent Reply:</span> {fb.agent_reply}
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Replied: {new Date(fb.agent_replied_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ Main HOD Dashboard ============
const HODDashboard = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const wsRef = useRef(null);
  
  // Get user role for filtering
  const userRole = user?.role || 'super_admin'; // Default to super_admin for full access
  
  // Determine available view modes based on user role
  const getAvailableViews = () => {
    // Admin roles get all departments
    const adminRoles = ['ceo', 'super_admin', 'admin', 'account_owner', 'owner', 'hod'];
    if (adminRoles.includes(userRole)) {
      return [
        { value: 'all', label: 'All Departments' },
        { value: 'sales', label: 'Sales' },
        { value: 'marketing', label: 'Marketing' }
      ];
    } else if (userRole === 'hod_sales') {
      return [{ value: 'sales', label: 'Sales' }];
    } else if (userRole === 'hod_marketing') {
      return [{ value: 'marketing', label: 'Marketing' }];
    }
    // Default to all departments for unknown roles
    return [
      { value: 'all', label: 'All Departments' },
      { value: 'sales', label: 'Sales' },
      { value: 'marketing', label: 'Marketing' }
    ];
  };
  
  const availableViews = getAvailableViews();
  // Sidebar links `?team=sales` / `?team=marketing` pre-select the
  // department. Role-based default still applies when no query param
  // is present (hod_sales → sales, hod_marketing → marketing, others → all).
  // If a team is supplied that the current role isn't allowed to see,
  // fall back to the role default so a URL can't broaden access.
  const [searchParams] = useSearchParams();
  const teamParam = (searchParams.get('team') || '').toLowerCase();
  const roleDefaultView =
    userRole === 'hod_sales'     ? 'sales' :
    userRole === 'hod_marketing' ? 'marketing' :
                                    'all';
  const allowedViewCodes = availableViews.map((v) => v.value);
  const defaultView = allowedViewCodes.includes(teamParam) ? teamParam : roleDefaultView;

  // Detect dark mode from DOM as fallback
  const [actualDarkMode, setActualDarkMode] = React.useState(false);
  const [selectedView, setSelectedView] = useState(defaultView);

  // Keep selectedView in sync with the URL when the sidebar navigates
  // between /hod-dashboard?team=sales and /hod-dashboard?team=marketing
  // while the page is already mounted (React Router doesn't remount the
  // same route, so we react to teamParam changes explicitly).
  useEffect(() => {
    if (allowedViewCodes.includes(teamParam) && teamParam !== selectedView) {
      setSelectedView(teamParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamParam]);

  // True when the role/department of a row belongs to the
  // currently-selected department dropdown. Centralised here so every
  // panel (alerts, agent status grid, team performance) applies the
  // same rule and stays in sync with the dropdown at the top.
  const matchesSelectedView = useCallback((roleStr, deptStr) => {
    if (selectedView === 'all') return true;
    const role = (roleStr || '').toLowerCase();
    const dept = (deptStr || '').toLowerCase();
    if (selectedView === 'sales') {
      return role.includes('sales') || role === 'cr_agent' || dept.includes('sales');
    }
    if (selectedView === 'marketing') {
      return role.includes('marketing') || dept.includes('marketing');
    }
    return true;
  }, [selectedView]);
  
  React.useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') || 
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
      setActualDarkMode(isDark);
    };
    checkDarkMode();
    
    // Listen for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);
  
  // Use either context or detected dark mode
  const darkMode = isDarkMode || actualDarkMode;

  // State
  const [overview, setOverview] = useState(null);
  const [dashboardFetchedAt, setDashboardFetchedAt] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [agentStatus, setAgentStatus] = useState([]);
  const [reports, setReports] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [chatUpdates, setChatUpdates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [ceoComments, setCeoComments] = useState([]);
  const [agentMovements, setAgentMovements] = useState([]);
  // Total count surfaced by UnifiedReportsTab — used by the Reports tab
  // badge so the number on the tab matches what's rendered inside.
  const [reportsTotal, setReportsTotal] = useState(0);
  // Sales + Marketing live snapshots — same shape the CEO panels render.
  // Fetched from the shared /api/dashboard/snapshot/* endpoints so HODs
  // see exactly the same numbers as the CEO, including operator-edited
  // targets (weekly sales goal, lead-gen target, monthly ad spend).
  const [salesSnapshot, setSalesSnapshot] = useState(null);
  const [marketingSnapshot, setMarketingSnapshot] = useState(null);
  // Date filter for the Agent In & Out tab. Empty = today (server default).
  const [movementsDate, setMovementsDate] = useState('');

  // ----- selectedView-aware filtered slices -----
  // When the user picks "Sales" or "Marketing" at the top, the cards
  // below should hide rows from the other department. We previously
  // only filtered the metric tiles, which is why a sales HOD viewing
  // "Sales" was still seeing marketing agents in the team grid, marketing
  // alerts, and the marketing snapshot panel. These memos fix that.
  const filteredAgentStatus = useMemo(
    () => agentStatus.filter((a) => matchesSelectedView(a.role, a.department)),
    [agentStatus, matchesSelectedView],
  );
  const filteredTeamPerformance = useMemo(
    () => teamPerformance.filter((a) => matchesSelectedView(a.role, a.department)),
    [teamPerformance, matchesSelectedView],
  );
  // Alerts don't carry a role directly — we look up the alert's
  // agent_id (or assigned_to) against the full agentStatus list to find
  // the agent's role, then filter from there. Alerts that don't tie to
  // any specific agent (org-wide) are always kept.
  const filteredAlerts = useMemo(() => {
    // The HOD dashboard's Alerts & Flags shows AGENT-subject alerts
    // only — alerts flagged against an HOD belong on the CEO dashboard.
    // The backend stamps subject_class on every alert.
    const agentOnly = alerts.filter((a) => a.subject_class !== 'hod');
    if (selectedView === 'all') return agentOnly;
    const roleById = {};
    const deptById = {};
    agentStatus.forEach((a) => {
      const id = a.id || a.agent_id;
      if (!id) return;
      roleById[id] = a.role;
      deptById[id] = a.department;
    });
    return agentOnly.filter((alert) => {
      const aid = alert.agent_id || alert.assigned_to;
      if (!aid) return true;
      return matchesSelectedView(roleById[aid], deptById[aid]);
    });
  }, [alerts, agentStatus, selectedView, matchesSelectedView]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Honour ?tab=… in the URL so notification click-through can deep-link
    // straight to (e.g.) the CEO Comments tab.
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const t = p.get('tab');
      if (t === 'comments') return 'ceo-comments';
      if (t) return t;
    }
    return 'overview';
  });
  // Lazy-but-persistent tab mounting. Only the currently-visited tab's
  // children render the first time around; previously-visited tabs stay
  // mounted (hidden via Radix's forceMount) so re-visiting them is an
  // instant CSS toggle with no reconciliation cost. Without this the
  // parent re-render on every tab click was reconciling six heavy
  // subtrees, which made switching feel laggy.
  const [mountedTabs, setMountedTabs] = useState(() => {
    const initial = new Set();
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const t = p.get('tab');
      if (t === 'comments') initial.add('ceo-comments');
      else if (t) initial.add(t);
      else initial.add('overview');
    } else {
      initial.add('overview');
    }
    return initial;
  });
  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  // Dialogs
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  // Chat modal — the "Comments to Agent" composer used to sit
  // inline in the tab content. It's now hidden by default and
  // opens as a floating dialog from the Chat button in the header,
  // matching the CEO Dashboard pattern.
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  // Alerts & Flags modal — surfaces the rules-based alert feed
  // (overdue follow-ups, silent agents, stuck deals, OOO without
  // return time) as a floating popup instead of taking up a fixed
  // column in the Overview tab. Same open-from-header pattern as
  // Chat, but the panel is threshold logic — no AI involved — so
  // the button and copy are named honestly.
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Fetch all dashboard data. Each endpoint resolves independently and
  // updates its own state — we DON'T wait for the slowest one before
  // rendering. Previously a single slow endpoint (typically the heavy
  // /overview or /reports calls) froze the whole HOD dashboard on a
  // full-page spinner for many seconds. Now the page paints as soon as
  // any data lands and slow sections fill in progressively.
  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    const headers = { 'Authorization': `Bearer ${token}` };
    // Helper: kick off a fetch, parse JSON, run the setter on resolve,
    // swallow errors so one bad endpoint doesn't take the rest down.
    const kick = (url, onData) => fetch(url, { headers })
      .then((r) => r.json())
      .then((data) => { if (data) onData(data); return data; })
      .catch((e) => {
        console.error(`[HOD_DASHBOARD] ${url} failed:`, e);
        return null;
      });

    // Spinner-off coordination — first endpoint to return flips it off,
    // and a 3-second safety timer caps the wait even if everything stalls.
    let firstFulfilled = false;
    const markRendered = () => {
      if (firstFulfilled) return;
      firstFulfilled = true;
      setLoading(false);
    };
    const safetyTimer = setTimeout(markRendered, 3000);

    // Comments + voice notes share a timeline — buffer separately and
    // re-merge whenever either updates, so they don't race each other.
    let textBuf = [];
    let voiceBuf = [];
    const userRole = user?.role || '';
    const matchesRole = (target) => !target || target === 'all' || target === userRole;
    const remergeComments = () => {
      const merged = [...textBuf, ...voiceBuf].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setCeoComments(merged);
    };

    const tasks = [
      kick(`${API_URL}/api/hod/overview`, (d) => { if (d.success) setOverview(d.data); }),
      kick(`${API_URL}/api/hod/team-performance`, (d) => { if (d.success) setTeamPerformance(d.data?.agents || []); }),
      kick(`${API_URL}/api/hod/agent-status`, (d) => { if (d.success) setAgentStatus(d.data?.agents || []); }),
      kick(`${API_URL}/api/hod/reports`, (d) => { if (d.success) setReports(d.data?.reports || []); }),
      kick(`${API_URL}/api/hod/alerts`, (d) => { if (d.success) setAlerts(d.data?.alerts || []); }),
      kick(`${API_URL}/api/hod/chat-updates?limit=20`, (d) => { if (d.success) setChatUpdates(d.data?.updates || []); }),
      kick(`${API_URL}/api/hod/tasks?limit=20`, (d) => { if (d.success) setTasks(d.data?.tasks || []); }),
      kick(`${API_URL}/api/hod/agent-movements${movementsDate ? `?date=${movementsDate}` : ''}`,
           (d) => { if (d.success) setAgentMovements(d.data?.movements || []); }),
      kick(`${API_URL}/api/dashboard/snapshot/sales?department=sales`,
           (d) => { if (d?.success) setSalesSnapshot(d.data); }),
      kick(`${API_URL}/api/dashboard/snapshot/marketing`,
           (d) => { if (d?.success) setMarketingSnapshot(d.data); }),
      kick(`${API_URL}/api/hod/ceo-comments`, (d) => {
        if (d.success) {
          textBuf = (d.data?.comments || []).map((c) => ({ ...c, _kind: 'text' }));
          remergeComments();
        }
      }),
      // Voice messages — token-on-querystring auth, not bearer header,
      // so this can't use the `kick` helper directly.
      fetch(`${API_URL}/api/voice-messages/by-chat-type/hod_comments?token=${encodeURIComponent(token)}&limit=100`)
        .then((r) => r.json())
        .then((d) => {
          if (d?.success) {
            voiceBuf = (d.messages || [])
              .filter((v) => matchesRole(v.target_hod))
              .map((v) => ({ ...v, _kind: 'voice' }));
            remergeComments();
          }
        })
        .catch(() => { /* non-fatal */ }),
    ];

    // First endpoint to resolve flips the spinner off.
    Promise.race(tasks).then(markRendered);
    // When everything settles, clear the safety timer and stamp the
    // last-fetched time so the Refresh icon stops spinning.
    Promise.allSettled(tasks).finally(() => {
      clearTimeout(safetyTimer);
      setDashboardFetchedAt(new Date());
    });
  }, [token, movementsDate, user?.role]);

  // Re-fetch movements when the user picks a different date on the In & Out
  // tab. The full dashboard refetch is heavy, so we just refresh that one
  // endpoint and update the local state.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/hod/agent-movements${movementsDate ? `?date=${movementsDate}` : ''}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        if (!cancelled && data?.success) {
          setAgentMovements(data?.data?.movements || []);
        }
      } catch (e) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [token, movementsDate]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token || !user?.id) return;

    const wsUrl = `${API_URL.replace('http', 'ws')}/ws/dashboard/${user.id}?token=${token}`;
    
    const connectWebSocket = () => {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('HOD Dashboard WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          switch (message.event) {
            case 'report_updated':
              // Add or update report in the list
              setReports(prev => {
                const existing = prev.findIndex(r => r.id === message.report_id);
                const newReport = {
                  id: message.report_id,
                  agent_id: message.agent_id,
                  agent: { full_name: message.agent_name },
                  report_text: message.report_text,
                  generated_at: message.generated_at,
                  trigger_type: message.trigger_type,
                  version: message.version,
                  hod_viewed: false
                };
                
                if (existing >= 0) {
                  const updated = [...prev];
                  updated[existing] = { ...updated[existing], ...newReport };
                  return updated;
                }
                return [newReport, ...prev];
              });
              toast.info(`${message.agent_name} updated their report`);
              break;

            case 'agent_ooo':
              setAgentStatus(prev => 
                prev.map(a => 
                  a.id === message.agent_id 
                    ? { 
                        ...a, 
                        current_status: 'ooo',
                        ooo_info: {
                          destination: message.destination,
                          reason: message.reason,
                          expected_return_at: message.expected_return_at
                        }
                      }
                    : a
                )
              );
              toast.info(`${message.agent_name} is now out of office`);
              break;

            case 'agent_returned':
              setAgentStatus(prev => 
                prev.map(a => 
                  a.id === message.agent_id 
                    ? { ...a, current_status: 'in_office', ooo_info: null }
                    : a
                )
              );
              toast.success(`${message.agent_name} has returned to office`);
              break;

            case 'task_completed':
              setTasks(prev => 
                prev.map(t => 
                  t.id === message.task_id 
                    ? { ...t, status: 'completed', completed_at: message.completed_at }
                    : t
                )
              );
              toast.success(`${message.agent_name} completed a task`);
              break;

            case 'chat_update_received':
              setChatUpdates(prev => [{
                id: message.chat_update_id,
                agent_id: message.agent_id,
                agent_name: message.agent_name,
                ai_summary: message.ai_summary,
                update_type: message.update_type,
                created_at: message.created_at
              }, ...prev].slice(0, 50));
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      ws.onclose = () => {
        console.log('HOD Dashboard WebSocket disconnected');
        // Attempt reconnect after 5 seconds
        setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token, user?.id]);

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Create task handler
  const handleCreateTask = async (taskData) => {
    setCreatingTask(true);
    try {
      const response = await fetch(`${API_URL}/api/hod/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taskData)
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Task assigned successfully');
        setIsTaskDialogOpen(false);
        setTasks(prev => [result.data.task, ...prev]);
      } else {
        toast.error(result.error || 'Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to assign task');
    }
    setCreatingTask(false);
  };

  // View report handler
  const handleViewReport = async (report) => {
    try {
      const response = await fetch(`${API_URL}/api/hod/reports/${report.agent_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setSelectedReport(result.data.report);
      }
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  // Add note to report
  const handleAddNote = async (reportId, noteText) => {
    try {
      const response = await fetch(`${API_URL}/api/hod/reports/${reportId}/note`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note_text: noteText })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Note added');
        setSelectedReport(prev => ({
          ...prev,
          hod_notes: [result.data.note, ...(prev.hod_notes || [])]
        }));
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Mark report as viewed by HOD
  const handleMarkViewed = async (reportId) => {
    try {
      const response = await fetch(`${API_URL}/api/hod/reports/${reportId}/mark-viewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Report marked as read');
        // Update selectedReport state
        setSelectedReport(prev => ({
          ...prev,
          hod_viewed: true,
          hod_viewed_at: new Date().toISOString()
        }));
        // Update reports list
        setReports(prev => prev.map(r => 
          r.id === reportId 
            ? { ...r, hod_viewed: true, hod_viewed_at: new Date().toISOString() }
            : r
        ));
      }
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  // Reply to CEO comment
  const handleReplyToComment = async (commentId, replyText) => {
    try {
      const response = await fetch(`${API_URL}/api/hod/ceo-comments/${commentId}/reply?reply_text=${encodeURIComponent(replyText)}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Reply sent to CEO');
        // Update local state
        setCeoComments(prev => prev.map(c => 
          c.id === commentId 
            ? { ...c, hod_reply: replyText, hod_replied_at: new Date().toISOString(), is_read_by_hod: true }
            : c
        ));
      } else {
        toast.error(result.error || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  // Handle conversation reply (for back-and-forth messages)
  const handleConversationReply = async (commentId, message) => {
    try {
      const response = await fetch(`${API_URL}/api/hod/ceo-comments/${commentId}/conversation`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Message sent');
        // Update local state with the returned comment (includes new conversation)
        setCeoComments(prev => prev.map(c => 
          c.id === commentId ? result.data.comment : c
        ));
      } else {
        toast.error(result.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  // Mark CEO comment as read
  const handleMarkCommentRead = async (commentId) => {
    try {
      await fetch(`${API_URL}/api/hod/ceo-comments/${commentId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCeoComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, is_read_by_hod: true } : c
      ));
    } catch (error) {
      console.error('Failed to mark comment as read:', error);
    }
  };

  // Mark all CEO comments as read
  const handleMarkAllCommentsRead = async () => {
    try {
      await fetch(`${API_URL}/api/hod/ceo-comments/mark-read-all`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCeoComments(prev => prev.map(c => ({ ...c, is_read_by_hod: true })));
      toast.success('All comments marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'}`}>
        <RefreshCw className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`} data-testid="hod-dashboard">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              HOD Dashboard
              {selectedView !== 'all' && (
                <span className={`ml-2 text-lg font-normal ${
                  selectedView === 'sales' ? 'text-blue-500' : 'text-purple-500'
                }`}>
                  - {selectedView === 'sales' ? 'Sales' : 'Marketing'}
                </span>
              )}
            </h1>
            <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              Team performance overview and real-time monitoring
            </p>
            {dashboardFetchedAt && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Data as of {dashboardFetchedAt.toLocaleString(undefined, {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })} · Auto-refresh every 60s
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Department / Role filter removed per CEO + admin
                request. The HOD landing on this page now sees the
                department implied by their role (sales HOD → Sales,
                marketing HOD → Marketing) without an explicit
                selector taking up header space. `selectedView` is
                still set automatically from the user's role via the
                `defaultView` initial state, so downstream filtering
                continues to work. */}
            <Button
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={async () => {
                await fetchDashboardData();
                toast.success('Dashboard refreshed');
              }}
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
            {/* Chat — opens the "Comments to Agent" composer as a
                floating modal. The panel used to live inline further
                down the tab; the Chat button gives HOD one-click
                access without leaving the current view. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsChatDialogOpen(true)}
              data-testid="hod-chat-btn"
            >
              <MessageSquare size={16} className="mr-2" />
              Chat
            </Button>
            {/* Alerts & Flags — rules-based feed (overdue follow-ups,
                silent agents, stuck deals, OOO w/o return). Live count
                badge doubles the button as a signal so the HOD notices
                new flags without the panel taking up screen space. */}
            <Button
              size="sm"
              onClick={() => setIsAlertsOpen(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="hod-alerts-btn"
            >
              <AlertTriangle size={16} className="mr-2" />
              Alerts & Flags
              {filteredAlerts.length > 0 && (
                <Badge className="ml-2 bg-white/25 text-white border-0 px-1.5 py-0 h-4 text-[10px]">
                  {filteredAlerts.length}
                </Badge>
              )}
            </Button>
            <Button onClick={() => setIsTaskDialogOpen(true)} data-testid="assign-task-btn">
              <Plus size={16} className="mr-2" />
              Assign Task
            </Button>
          </div>
        </div>

        {/* CRM Overview */}
        <div className="mb-6">
          <CRMOverviewPanel data={overview} loading={loading} selectedView={selectedView} />
        </div>

        {/* Marketing Dashboard — embedded as the headline section
            for HOD Marketing. Same component the standalone
            /marketing-dashboard route used to render. Pulling it in
            here lets us drop the duplicate sidebar entry and means
            marketing leadership lands on the right view by default. */}
        {(() => {
          const role = (user?.role || '').toLowerCase();
          const isMarketingContext = role.includes('marketing') || selectedView === 'marketing';
          if (!isMarketingContext) return null;
          return (
            <div className="mb-6">
              {/* embedded=true drops the standalone page chrome
                  (min-h-screen + outer max-w + p-6) so the panels
                  flow tightly under the HOD page header instead of
                  inheriting a full-viewport spacer. */}
              <MarketingDashboardV2 embedded={true} />
            </div>
          );
        })()}

        {/* Phase 1: Agent Status + Response Time Row
            Marketing context (either the user's own role is marketing
            or they've filtered the dashboard to Marketing) doesn't
            need the field-tracking Agent Status panel — marketing
            staff aren't tracked by geofence. We keep Response Time
            since SLA matters across departments. */}
        {(() => {
          const role = (user?.role || '').toLowerCase();
          const isMarketingContext = role.includes('marketing') || selectedView === 'marketing';
          if (isMarketingContext) {
            return (
              <div className="grid grid-cols-1 gap-6 mb-6">
                <ResponseTimeCard compact={true} departmentFilter={selectedView} />
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <AgentStatusDashboard compact={true} departmentFilter={selectedView} />
              <ResponseTimeCard compact={true} departmentFilter={selectedView} />
            </div>
          );
        })()}

        {/* Team SLA monitor — sits above the tab bar so SLA performance
            is visible regardless of which tab is active. Self-fetches
            from /api/dashboard/manager-sla and gates itself server-side
            to manager+ roles. */}
        <TeamSLAMonitor />

        {/* Tabs row hoisted to the top so the HOD can switch views without
            scrolling past the dashboards. Live Agent Status, Alerts, the
            location map and the GPS panel render inline below the tabs and
            stay visible on every tab — only TabsContent rows swap. */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 size={16} />
              Overview
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Users size={16} />
              {/* Use filteredTeamPerformance — that's the list the
                  Team Performance table actually renders. Was using
                  agentStatus.length, which is a different (smaller)
                  feed (Live Agent Status grid), so the tab counter
                  said "(2)" while the table showed 10 rows.
                  Marketing HODs see "Marketing Users" instead of
                  "Agents" — agent is a sales-floor word and reads
                  as wrong-context for a marketing manager. */}
              {(() => {
                const r = (user?.role || '').toLowerCase();
                const isMarketing = r.includes('marketing') || selectedView === 'marketing';
                return `${isMarketing ? 'Marketing Users' : 'Agents'} (${filteredTeamPerformance.length})`;
              })()}
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText size={16} />
              {/* Total counter reflects daily reports + site-visit
                  reports merged — same number the panel below shows.
                  Was `reports.length` which only counted daily reports. */}
              Reports ({reportsTotal})
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <Activity size={16} />
              Tasks ({tasks.length})
            </TabsTrigger>
            {/* "In & Out" tracks field check-ins / check-outs — only
                shown for non-marketing context. Marketing roles don't
                go on geofenced site visits, so the tab is hidden for
                them along with the rest of the field-tracking widgets. */}
            {(() => {
              const role = (user?.role || '').toLowerCase();
              const isMarketingContext = role.includes('marketing') || selectedView === 'marketing';
              if (isMarketingContext) return null;
              return (
                <TabsTrigger value="movements" className="flex items-center gap-2">
                  <MapPin size={16} />
                  In & Out ({agentMovements.length})
                </TabsTrigger>
              );
            })()}
            <TabsTrigger value="ceo-comments" className="flex items-center gap-2">
              <Crown size={16} />
              CEO Comments
              {ceoComments.filter(c => !c.is_read_by_hod).length > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">
                  {ceoComments.filter(c => !c.is_read_by_hod).length}
                </Badge>
              )}
            </TabsTrigger>
            {/* Marketing Dashboard — an extra tab for the Marketing HOD
                only. Added alongside the existing tabs, which are
                untouched. */}
            {userRole === 'hod_marketing' && (
              <TabsTrigger value="marketing" className="flex items-center gap-2">
                <BarChart3 size={16} />
                Marketing
              </TabsTrigger>
            )}
          </TabsList>

          {/* Overview Tab — Live Agent Status + Alerts when this tab is
              selected. Map / GPS / Composer / Latest Agent Reports live in
              the always-visible block below the Tabs. */}
          <TabsContent value="overview" className="space-y-6" forceMount={true} hidden={activeTab !== 'overview'}>
            {mountedTabs.has('overview') && (
            <div>
              {/* Alerts & Flags used to live in the right column here;
                  it's now behind the "AI Insights" button in the page
                  header, freeing the Overview tab to focus on team
                  activity. Agent Status expands to full width. */}
              <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <Users size={18} />
                    Live Agent Status
                    <HelpInfo
                      align="inline"
                      title="How to read Live Agent Status"
                      steps={[
                        <>Each card shows an agent and their current state: <em>Active</em> (in office or in field, logging activity), <em>No Activity</em> (nothing logged recently), or <em>On Leave</em>.</>,
                        'Their Leads / Closed / Conversion numbers give a quick read on output without leaving the page.',
                        'Click an agent card to drill into their full profile and lead list.',
                        'The list respects the Sales / Marketing dropdown at the top — switch to "Marketing" to see only the marketing team.',
                      ]}
                      tip="Agents stuck on No Activity for half a day are the ones to nudge first."
                    />
                  </CardTitle>
                  <CardDescription>Real-time status of your team members</CardDescription>
                </CardHeader>
                <CardContent>
                  <AgentStatusGrid
                    agents={filteredAgentStatus}
                    loading={loading}
                    onViewAgent={(agent) => console.log('View agent:', agent)}
                  />
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>

          {/* Agents Tab — Marketing HOD sees the same component with a
              different column set: Leads Sourced / Qualified % / Closed
              Attribution / CPL instead of Leads / Deals Closed /
              Conv. Rate / Avg. Cycle. The data source (filteredTeamPerformance)
              is unchanged — we just relabel + remap the cells so a
              marketing manager isn't staring at "Avg. Cycle (days)"
              which is a sales-pipeline construct that doesn't apply
              to campaign work. */}
          <TabsContent value="agents" forceMount={true} hidden={activeTab !== 'agents'}>
            {mountedTabs.has('agents') && (() => {
              const r = (user?.role || '').toLowerCase();
              const isMarketing = r.includes('marketing') || selectedView === 'marketing';
              const headerLabel = isMarketing ? 'Marketing Users' : 'Agents';
              const noun = isMarketing ? 'marketing user' : 'agent';
              return (
            <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <Users size={18} />
                  Team Performance
                  <HelpInfo
                    align="inline"
                    title={`How to read ${isMarketing ? 'Marketing User' : 'Team'} Performance`}
                    steps={isMarketing ? [
                      'One row per marketing user on your team. Columns: leads sourced from their campaigns, qualified rate, closed-won attribution, cost per lead (CPL), and last active time.',
                      <>Sort by clicking column headers — try <em>Qualified %</em> to spot the users sourcing the highest-quality leads, or <em>CPL</em> to spot inefficient spend.</>,
                      'The Status badge reflects what the user is doing right now: Active means they\'re logging activity; No Activity means they\'ve been quiet today.',
                      <>Pair with the <em>Sales / Marketing</em> dropdown at the top to flip the view back to the sales-flavour table.</>,
                    ] : [
                      'One row per agent on your team. Columns: leads assigned, deals closed this month, conversion rate, average deal cycle, last active time.',
                      <>Sort by clicking column headers — try sorting by <em>Conv. Rate</em> to spot the top performers, or <em>Last Active</em> to find inactive agents.</>,
                      'The Status badge reflects what the agent is doing right now: Active means they\'re logging activity; No Activity means they\'ve been quiet today.',
                      'Pair with the Sales / Marketing dropdown at the top to focus on one department.',
                    ]}
                    tip={isMarketing
                      ? 'High lead volume with low qualified % is the classic "spray and pray" pattern — those campaigns need tighter targeting, not more budget.'
                      : 'A long average cycle with low conversion is the classic "burning leads" pattern — those agents need coaching, not nudging.'
                    }
                  />
                </CardTitle>
                <CardDescription>Detailed performance metrics for each {noun}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-border' : 'border-slate-200'}`}>
                        <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                          {headerLabel.slice(0, -1) /* singular */}
                        </th>
                        <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</th>
                        {isMarketing ? (
                          <>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Leads Sourced</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Qualified %</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Closed Attrib.</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>CPL</th>
                          </>
                        ) : (
                          <>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Leads</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Deals Closed</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Conv. Rate</th>
                            <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Avg. Cycle</th>
                          </>
                        )}
                        <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTeamPerformance.map((agent) => (
                        <tr key={agent.id} className={`border-b ${darkMode ? 'border-border' : 'border-slate-100'}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center text-white text-sm font-medium">
                                {agent.full_name?.charAt(0) || '?'}
                              </div>
                              <span className={darkMode ? 'text-white' : 'text-slate-900'}>
                                {agent.full_name || agent.email}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {/* Three-state status, colour-matched to the
                                Agent Status panel: In Office = green,
                                In Field = red, On Leave = blue. There is
                                no "offline" — anyone not in-office and
                                not on-leave is treated as in the field. */}
                            {(() => {
                              const v = (agent.current_status || '').toLowerCase();
                              const view =
                                ['in_office', 'inside', 'in office'].includes(v)
                                  ? { label: 'In Office', dot: 'bg-emerald-500', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' }
                                : ['on_leave', 'leave', 'on leave'].includes(v)
                                  ? { label: 'On Leave', dot: 'bg-blue-500', cls: 'bg-blue-50 text-blue-700 border-blue-300' }
                                : { label: 'In Field', dot: 'bg-rose-500', cls: 'bg-rose-50 text-rose-700 border-rose-300' };
                              return (
                                <Badge className={`text-xs border ${view.cls}`}>
                                  <span className={`w-2 h-2 rounded-full ${view.dot} mr-1.5`} />
                                  {view.label}
                                </Badge>
                              );
                            })()}
                          </td>
                          {isMarketing ? (
                            <>
                              {/* Marketing columns. We map the existing
                                  shared fields onto marketing-flavoured
                                  meanings — leads_assigned counts the
                                  leads attributed to this user's campaigns,
                                  conversion_rate is repurposed as the
                                  qualified-percentage (close enough for
                                  this surface; the per-user marketing
                                  metrics service will populate exact
                                  numbers when wired). CPL shows '—' when
                                  no spend data is present rather than
                                  fabricating a fake zero. */}
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.leads_assigned || 0}
                              </td>
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.qualified_pct != null
                                  ? `${agent.qualified_pct}%`
                                  : (agent.conversion_rate != null ? `${agent.conversion_rate}%` : '—')}
                              </td>
                              <td className={`py-3 px-4 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.deals_closed || 0}
                              </td>
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {agent.cpl != null && agent.cpl > 0
                                  ? `AED ${Number(agent.cpl).toLocaleString()}`
                                  : '—'}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.leads_assigned || 0}
                              </td>
                              <td className={`py-3 px-4 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.deals_closed || 0}
                              </td>
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {agent.conversion_rate || 0}%
                              </td>
                              <td className={`py-3 px-4 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {agent.avg_deal_cycle_days || 0} days
                              </td>
                            </>
                          )}
                          <td className={`py-3 px-4 text-center text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {agent.last_active
                              ? new Date(agent.last_active).toLocaleString()
                              : 'No activity'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
              );
            })()}
          </TabsContent>

          {/* Reports Tab — daily reports + site-visit reports unified, with
              date-range, agent and type filters plus CSV download. */}
          <TabsContent value="reports" forceMount={true} hidden={activeTab !== 'reports'}>
            {mountedTabs.has('reports') && (
              <UnifiedReportsTab
                dailyReports={reports}
                agents={agentStatus}
                onTotalChange={(c) => setReportsTotal(c.total)}
              />
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" forceMount={true} hidden={activeTab !== 'tasks'}>
            {mountedTabs.has('tasks') && (
            <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <Activity size={18} />
                    Assigned Tasks
                    <HelpInfo
                      align="inline"
                      title="How to use Assigned Tasks"
                      steps={[
                        'Every task you create for an agent shows here — title, owner, priority and due date.',
                        <>Click <em>New Task</em> on the right to draft a task. You'll pick an agent, set a priority (Low / Medium / High) and a due date; the agent gets a bell notification and the task appears on their My Dashboard.</>,
                        'Tasks change status as the agent works through them (Open → In progress → Done). Overdue ones turn red so they stand out.',
                        'Click any task to reopen the editor — you can adjust the priority, push the date, or cancel it. Cancellation is soft so you can still see what you asked for.',
                      ]}
                      tip="Two or three concrete tasks per agent per week beats a long backlog — it keeps follow-ups inside the dashboard instead of in chat."
                    />
                  </CardTitle>
                  <CardDescription>Tasks you've assigned to your team</CardDescription>
                </div>
                <Button size="sm" onClick={() => setIsTaskDialogOpen(true)}>
                  <Plus size={16} className="mr-2" />
                  New Task
                </Button>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <div className={`text-center py-12 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Activity size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No tasks assigned yet</p>
                    <Button className="mt-4" onClick={() => setIsTaskDialogOpen(true)}>
                      Assign First Task
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.map((task) => (
                      <div 
                        key={task.id}
                        className={`p-4 rounded-lg border ${
                          task.status === 'completed'
                            ? darkMode ? 'bg-emerald-900/10 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'
                            : task.status === 'overdue'
                            ? darkMode ? 'bg-red-900/10 border-red-700/50' : 'bg-red-50 border-red-200'
                            : darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {task.title}
                              </p>
                              <Badge variant={task.priority === 'high' ? 'destructive' : task.priority === 'medium' ? 'warning' : 'secondary'} className="text-xs">
                                {task.priority}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              Assigned to: {task.agent?.full_name || task.agent_id}
                            </p>
                            {task.description && (
                              <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {task.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={
                              task.status === 'completed' ? 'success' : 
                              task.status === 'overdue' ? 'destructive' : 'outline'
                            } className="text-xs">
                              {task.status}
                            </Badge>
                          </div>
                        </div>
                        <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {task.due_date && `Due: ${task.due_date} ${task.due_time || ''}`}
                          {task.completed_at && ` • Completed: ${new Date(task.completed_at).toLocaleString()}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Agent Movements / In & Out Tab — hidden in marketing
              context (the matching TabsTrigger above is also hidden,
              so this content can't be reached, but we still skip
              mounting to keep the DOM tidy). */}
          <TabsContent value="movements" forceMount={true} hidden={activeTab !== 'movements'}>
            {(() => {
              const role = (user?.role || '').toLowerCase();
              const isMarketingContext = role.includes('marketing') || selectedView === 'marketing';
              if (isMarketingContext) return null;
              return (
                <>
            {mountedTabs.has('movements') && (
            <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <MapPin size={18} />
                      Agent In & Out
                      <HelpInfo
                        align="inline"
                        title="How to read Agent In & Out"
                        steps={[
                          'One row per agent for the selected day, showing every time they left office and returned — destination, reason and total time out.',
                          'The date picker on the right defaults to today. Switch dates to audit a past day; the "Today" button snaps back instantly.',
                          <>Each row shows total minutes <em>out</em>. Long gaps without a logged return tell you the agent forgot to check in — open the visit to see the GPS trace.</>,
                          'Rows are coloured by whether the visit closed cleanly, is still open, or had GPS issues — a quick visual sweep tells you which agents need a nudge.',
                        ]}
                        tip="Use this with the field-visit policy: anyone with multiple unclosed visits in a week is worth a 1:1."
                      />
                    </CardTitle>
                    <CardDescription>Track agent field visits and check-in/out status</CardDescription>
                  </div>
                  {/* Date picker — defaults to today, lets the HOD scroll
                      back through past days. Empty resets to today. */}
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar size={14} className="text-slate-400" />
                    <input
                      type="date"
                      value={movementsDate}
                      onChange={(e) => setMovementsDate(e.target.value)}
                      className={`h-8 px-2 rounded border ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                    />
                    {movementsDate && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setMovementsDate('')}>
                        Today
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {agentMovements.length === 0 ? (
                  <div className={`text-center py-12 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <MapPin size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No movement data available</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          <th className={`text-left py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Agent</th>
                          <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</th>
                          <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Check In</th>
                          <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Check Out</th>
                          <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Location</th>
                          <th className={`text-center py-3 px-4 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Alert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentMovements.map((movement, idx) => (
                          <tr key={movement.id || idx} className={`border-b ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                            <td className={`py-3 px-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Users size={14} className="text-blue-500" />
                                <span>{movement.agent_name}</span>
                                {movement.trip_total > 1 && (
                                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                    Trip {movement.trip_number} of {movement.trip_total}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {movement.status === 'in_field' ? (
                                <Badge className="bg-emerald-500">
                                  <MapPin size={12} className="mr-1" /> In Field
                                </Badge>
                              ) : movement.status === 'in_office' ? (
                                <Badge className="bg-blue-500">
                                  <Building size={12} className="mr-1" /> In Office
                                </Badge>
                              ) : movement.status === 'report_submitted' ? (
                                <div className="flex items-center gap-1 justify-center flex-wrap">
                                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                                    <CheckCircle2 size={12} className="mr-1" /> Report Submitted
                                  </Badge>
                                  <AgentMovementOpenReportButton movement={movement} darkMode={darkMode} />
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-amber-500 border-amber-500">
                                  <AlertTriangle size={12} className="mr-1" /> Not Updated
                                </Badge>
                              )}
                            </td>
                            <td className={`py-3 px-4 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {movement.check_in ? (
                                <div className="flex items-center justify-center gap-1">
                                  <LogIn size={12} className="text-emerald-500" />
                                  {new Date(movement.check_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              ) : (
                                <span className="text-amber-500">--:--</span>
                              )}
                            </td>
                            <td className={`py-3 px-4 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {movement.check_out ? (
                                <div className="flex items-center justify-center gap-1">
                                  <LogOut size={12} className="text-red-500" />
                                  {new Date(movement.check_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              ) : (
                                <span className="text-slate-400">--:--</span>
                              )}
                            </td>
                            <td className={`py-3 px-4 text-center text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {movement.location || 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {movement.status === 'not_updated' ? (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle size={10} className="mr-1" />
                                  No Update
                                </Badge>
                              ) : (
                                <CheckCircle2 size={16} className="mx-auto text-emerald-500" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
                </>
              );
            })()}
          </TabsContent>

          {/* CEO Comments Tab */}
          <TabsContent value="ceo-comments" forceMount={true} hidden={activeTab !== 'ceo-comments'}>
            {mountedTabs.has('ceo-comments') && (
            <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''} id="ceo-comments">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  <Crown size={18} className="text-violet-500" />
                  Comments from CEO
                  {ceoComments.filter(c => !c.is_read_by_hod).length > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {ceoComments.filter(c => !c.is_read_by_hod).length} unread
                    </Badge>
                  )}
                  <HelpInfo
                    align="inline"
                    title="How to use Comments from CEO"
                    steps={[
                      'Every direct message from the CEO to you (or to all HODs) lands here — text comments and voice notes.',
                      'Unread comments are highlighted at the top; the red badge in the tab title shows the unread count.',
                      'Reply inline — your response posts back into the same thread the CEO sees.',
                      'You can record a voice reply for nuance, or escalate by tagging team members in your text reply.',
                      'Mark all as read clears the unread highlights once you\'ve worked through them.',
                    ]}
                    tip="Reply to urgent comments within an hour — the CEO sees response time in their own dashboard."
                  />
                </CardTitle>
                <CardDescription>Private communication channel with CEO</CardDescription>
              </CardHeader>
              <CardContent>
                <CEOCommentsPanel
                  comments={ceoComments}
                  loading={loading}
                  onReply={handleReplyToComment}
                  onMarkRead={handleMarkCommentRead}
                  onMarkAllRead={handleMarkAllCommentsRead}
                  userRole={userRole}
                  onConversationReply={handleConversationReply}
                />
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Marketing Dashboard tab — Marketing HOD only. Extra tab
              that sits alongside the existing ones without changing
              them. Wrapped in one bordered box so it's visually clear
              where the Marketing tab's content ends — the always-on
              "Live Marketing Snapshot" panel below sits OUTSIDE this
              box, so it's no longer mistaken for part of this tab. */}
          {userRole === 'hod_marketing' && (
            <TabsContent value="marketing" forceMount={true} hidden={activeTab !== 'marketing'}>
              {mountedTabs.has('marketing') && (
                <div className={`rounded-xl border-2 p-4 ${
                  darkMode ? 'border-violet-700/50 bg-violet-900/10' : 'border-violet-200 bg-violet-50/40'
                }`}>
                  <MarketingDashboardTab />
                  <p className={`text-center text-xs mt-4 pt-3 border-t ${
                    darkMode ? 'text-slate-500 border-violet-700/40' : 'text-slate-400 border-violet-200'
                  }`}>
                    — End of Marketing Dashboard —
                  </p>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Always-visible panels below the tab bar — render after the Tabs
            block so they appear on every tab regardless of which one is
            active. (Live Agent Status + Alerts moved into the Overview tab
            so it shows when that tab is selected.) */}
        <div className="space-y-6 mt-6">
          {/* Live Sales + Marketing snapshots — same panels the CEO sees.
              HODs can edit the underlying targets via the Edit Targets
              button on each panel; save calls PUT /api/dashboard/targets
              and we re-run fetchDashboardData to repaint with the new
              progress bars. Sales HODs only see the Sales tile, marketing
              HODs only see the Marketing tile, and 'hod' / admin roles
              see both side-by-side like the CEO overview. */}
          {/* Snapshot panels — honour BOTH the user's role (a sales HOD
              never sees Marketing data) AND the department dropdown the
              user picked at the top (an admin viewing "Sales" hides the
              marketing tile even though their role would normally see
              it). Without the dropdown gate, picking "Sales" was leaving
              the Marketing Snapshot visible. */}
          {(() => {
            const showSales = userRole !== 'hod_marketing'
              && (selectedView === 'all' || selectedView === 'sales');
            const showMarketing = userRole !== 'hod_sales'
              && (selectedView === 'all' || selectedView === 'marketing');
            const bothVisible = showSales && showMarketing;
            return (
              <div className={`grid grid-cols-1 ${bothVisible ? 'lg:grid-cols-2' : ''} gap-6`}>
                {showSales && (
                  <SharedSalesSnapshotPanel
                    data={salesSnapshot}
                    token={token}
                    onTargetsSaved={fetchDashboardData}
                  />
                )}
                {showMarketing && (
                  <SharedMarketingSnapshotPanel
                    data={marketingSnapshot}
                    token={token}
                    onTargetsSaved={fetchDashboardData}
                  />
                )}
              </div>
            );
          })()}

          {/* Field-tracking widgets (Live Agent Tracker map, Team
              Site Visits & Live Locations, Team Site Visit Reports)
              are sales-only — marketing roles don't go on site visits
              and don't get geo-tracked. Hide the whole stack when the
              user is in a marketing context. */}
          {(() => {
            const role = (user?.role || '').toLowerCase();
            const isMarketingContext = role.includes('marketing') || selectedView === 'marketing';
            if (isMarketingContext) return null;
            return (
              <>
                <LiveLocationMap compact={false} agentTypeFilter="all" roleScope="agents_only" />
                <TeamSiteVisitsPanel />
                {/* Team Site Visit Reports — the whole team's submitted
                    visit reports, sitting directly below Team Site Visits &
                    Live Locations. Team scope shows every agent's reports
                    with the inline Feedback action. */}
                <SiteVisitReportsPanel scope="team" title="Team Site Visit Reports" />
                {/* Agent Talktime — per-agent call performance over the
                    selected window. Sales-only widget; sits directly
                    below Team Site Visit Reports so the HOD scans
                    "where are agents physically?" and "are they on the
                    phone?" in one visual sweep. */}
                <AgentTalktimePanel />
              </>
            );
          })()}

          {/* Comments to Agent — moved out of the inline tab layout
              into a floating dialog opened from the Chat button in
              the page header. See the Dialog near the bottom of this
              component. */}

          {/* "My Site Visit Reports" (scope="mine") intentionally lives
              only on My Dashboard (the agent's SalesReportingDashboard)
              — the HOD page shows the team-scoped panel above instead. */}

          {/* Latest Agent Reports — always visible below the composer. */}
          <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <FileText size={18} />
                Latest Agent Reports
                <HelpInfo
                  align="inline"
                  title="How to read Latest Agent Reports"
                  steps={[
                    'A live feed of the five most-recent reports submitted across your team — daily reports and site-visit reports mixed together.',
                    <>Each row shows the agent, the report type and a one-line preview. Click the row to open the full report in a side drawer.</>,
                    'Reports auto-refresh as agents submit — you don\'t need to reload the page.',
                    <>For the full searchable history (with date range, agent filter, and CSV export), use the <em>Reports</em> tab above.</>,
                  ]}
                  tip="If a familiar agent stops appearing here for a couple of days, it's usually because they're not submitting — check before assuming they're slacking."
                />
              </CardTitle>
              <CardDescription>Real-time updates from your team</CardDescription>
            </CardHeader>
            <CardContent>
              <LiveReportFeed
                reports={reports.slice(0, 5)}
                loading={loading}
                onViewReport={handleViewReport}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      {/* Pass the FILTERED list so HOD Marketing sees only marketing
          users in the dropdown — the unfiltered `agentStatus` would
          include sales agents that an HOD Marketing has no business
          assigning tasks to. The noun flips to "Marketing User" so
          the dialog reads naturally for marketing context. */}
      {(() => {
        const r = (user?.role || '').toLowerCase();
        const isMarketing = r.includes('marketing') || selectedView === 'marketing';
        return (
          <CreateTaskDialog
            agents={filteredAgentStatus}
            noun={isMarketing ? 'Marketing User' : 'Agent'}
            isOpen={isTaskDialogOpen}
            onClose={() => setIsTaskDialogOpen(false)}
            onCreateTask={handleCreateTask}
            creating={creatingTask}
          />
        );
      })()}

      {/* Chat dialog — floating "Comments to Agent" composer +
          thread. Reuses the exact CommentsToAgentPanel component so
          send / voice-note behaviour is identical to the old inline
          panel. Opens from the Chat button in the page header. */}
      <Dialog
        open={isChatDialogOpen}
        onOpenChange={(open) => setIsChatDialogOpen(!!open)}
      >
        <DialogContent className={`max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
          <DialogHeader className={`px-6 pt-6 pb-2 border-b ${isDarkMode ? 'border-slate-700' : ''}`}>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare size={18} className="text-violet-500" />
              HOD Chat
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto p-6 flex-1">
            <CommentsToAgentPanel />
          </div>
        </DialogContent>
      </Dialog>

      {/* Alerts & Flags dialog — rules-based feed of team issues
          (overdue follow-ups, silent agents, stuck deals, OOO without
          return time, etc.). Named honestly — no AI is involved; it's
          threshold logic applied to the CRM state. Same data + same
          interactions as the old inline panel — just off the main
          scroll path so the Overview tab stays focused. */}
      <Dialog
        open={isAlertsOpen}
        onOpenChange={(open) => setIsAlertsOpen(!!open)}
      >
        <DialogContent className={`max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 ${isDarkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
          <DialogHeader className={`px-6 pt-6 pb-4 border-b ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'bg-gradient-to-r from-violet-50 to-white'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                  Alerts & Flags
                  {filteredAlerts.length > 0 && (
                    <Badge variant="destructive" className="text-[10px]">{filteredAlerts.length}</Badge>
                  )}
                </DialogTitle>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Live alerts + flags across your team — click any row to send Feedback or Warning.
                </p>
              </div>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto p-6 flex-1">
            <AlertsFlagsPanel alerts={filteredAlerts} loading={loading} token={token} />
          </div>
        </DialogContent>
      </Dialog>

      <ReportDetailDialog
        report={selectedReport}
        isOpen={!!selectedReport}
        onClose={() => setSelectedReport(null)}
        onAddNote={handleAddNote}
        onMarkViewed={handleMarkViewed}
      />
    </div>
  );
};

export default HODDashboard;
